import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async action (usually an API call) so it runs once at a time:
 * calls made while it is in flight are dropped, and `busy` is true until it
 * settles so the control that started it can show it registered. `<Button>`
 * does this by itself when its onClick returns the promise; use this for
 * plain `<button>`s, cards and anything else that starts a request.
 */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R> | R) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const latest = useRef(fn);
  latest.current = fn;
  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setBusy(true);
    try {
      return await latest.current(...args);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);
  return [run, busy] as const;
}
